import React from 'react';

import { useQuery } from '@tanstack/react-query';

import { getActiveUserOptions } from '@api/users/api.users.options';
import { useActiveUser } from '@api/users/api.users.queries';
import { ROLES } from '@permissions';

import { AuthProviderContext } from './AuthProvider.context';

import type {
  AuthContextProperties,
  AuthProviderProperties,
} from './AuthProvider.types';
import type { CurrentUserFetchResponse } from '@api/users/api.users.types';

// Дефолтный контекст для неавторизованных пользователей (landing page)
const defaultAuthContext: AuthContextProperties = {
  id: '',
  email: '',
  first_name: '',
  last_name: '',
  full_name: '',
  role: ROLES.unset,
  phone_number: '',
  phone_number_country: '',
  settings: {
    date_format: '',
    number_format: 'ru-RU',
    locales: 'ru',
    theme: 'light',
  },
  email_verified: false,
  phone_verified: false,
  is_verified: false,
  data_complete: false,
  is_authenticated: false,
  is_active: false,
  tenants: [],
  user_carrot_quest_hash: '',
};

const PUBLIC_PATH_PREFIXES = [
  '/landing',
  '/for-whom',
  '/features',
  '/example',
] as const;

/*
 * Проверки `if (globalThis.window === undefined) return false;` здесь больше
 * нет (AF-351). Она защищала от исполнения вне браузера, а такого исполнения у
 * приложения не бывает: сборка одна и клиентская (`vite build`, SPA, никакого
 * SSR и пререндера), а тесты идут в `happy-dom` (`vite.config.ts`,
 * `test.environment`). То есть `globalThis.window` определён всегда, ложная
 * ветвь была не «не проверена», а неисполнима.
 *
 * Вернуть проверку нужно, если у приложения появится серверный рендер или этот
 * модуль начнут импортировать в node-скрипте.
 */
const isPublicPathname = (): boolean => {
  const pathname = globalThis.location.pathname;
  return (
    pathname === '/' ||
    PUBLIC_PATH_PREFIXES.some(prefix => pathname.startsWith(prefix))
  );
};

// Компонент который загружает пользователя
const AuthProviderWithUser: React.FC<AuthProviderProperties> = ({
  children,
}) => {
  const { data: user } = useActiveUser();

  const value = React.useMemo<AuthContextProperties>(() => {
    return user;
  }, [user]);

  return (
    <AuthProviderContext.Provider value={value}>
      {children}
    </AuthProviderContext.Provider>
  );
};

// Компонент без загрузки пользователя для публичных страниц
const AuthProviderWithoutUser: React.FC<AuthProviderProperties> = ({
  children,
}) => {
  return (
    <AuthProviderContext.Provider value={defaultAuthContext}>
      {children}
    </AuthProviderContext.Provider>
  );
};

export const AuthProvider: React.FC<AuthProviderProperties> = ({
  children,
}) => {
  // Подписка на кэш activeUser без автоматического fetch.
  // Когда handleUserAuthSuccess вызывает setQueryData(activeUser, user),
  // этот hook обновляется → AuthProvider переключается на WithUser.
  // Это решает баг: при старте с landing page (isPublicPage=true навсегда)
  // аватар не обновлялся после логина, т.к. useMemo([]) не пересчитывался.
  const { data: cachedUser } = useQuery<CurrentUserFetchResponse>({
    ...getActiveUserOptions,
    enabled: false,
  });

  // Если в кэше есть аутентифицированный пользователь — всегда грузим полный провайдер.
  // Это покрывает сценарий: пользователь зашёл с landing → залогинился → redirect на /app.
  if (cachedUser?.is_authenticated) {
    return <AuthProviderWithUser>{children}</AuthProviderWithUser>;
  }

  // Для публичных страниц без аутентифицированного пользователя — дефолтный контекст
  // (оптимизация: не делаем GET /users/me на landing pages)
  if (isPublicPathname()) {
    return <AuthProviderWithoutUser>{children}</AuthProviderWithoutUser>;
  }

  // Для защищённых страниц — загружаем пользователя
  return <AuthProviderWithUser>{children}</AuthProviderWithUser>;
};
